import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Turn a backend-relative media path (e.g. "/api/track-image/<hash>.png") into
// an absolute URL against the backend, leaving absolute URLs untouched.
export function mediaUrl(path) {
  if (!path || typeof path !== "string") return path;
  if (/^(https?:)?\/\//.test(path)) return path;
  if (!path.startsWith("/")) return path;
  return `${BACKEND_URL || ""}${path}`;
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
});

// Request interceptor - add auth token
api.interceptors.request.use((config) => {
  // If a caller already attached an Authorization header (e.g. coach dashboard),
  // respect it and don't overwrite.
  if (config.headers?.Authorization) {
    return config;
  }

  // For member endpoints, use member token
  // For admin endpoints, use admin token
  // For coach endpoints, use coach token
  // This prevents issues when multiple tokens exist
  const adminToken = localStorage.getItem("sagt_token");
  const memberToken = localStorage.getItem("sagt_member_token");
  const coachToken = localStorage.getItem("coach_token");
  
  let token = null;
  if (config.url?.includes('/coach/')) {
    // Coach endpoints must use coach token only — never admin/member fallback
    token = coachToken;
  } else if (config.url?.includes('/member/')) {
    // Member endpoints should use member token
    token = memberToken || adminToken;
  } else if (config.url?.includes('/world/')) {
    // SAGT World endpoints use the member (account) token
    token = memberToken || adminToken;
  } else if (config.url?.includes('/admin/')) {
    // Admin endpoints should use admin token
    token = adminToken;
  } else {
    // Other endpoints - prefer admin token if exists
    token = adminToken || memberToken;
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle errors and retry on network issues
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // For 401 errors on member endpoints, check if token exists but wasn't sent
    // This can happen on Safari due to localStorage timing
    if (error.response?.status === 401 && config.url?.includes('/member/') && !config._authRetry) {
      const memberToken = localStorage.getItem("sagt_member_token");
      if (memberToken && !config.headers.Authorization?.includes(memberToken)) {
        config._authRetry = true;
        config.headers.Authorization = `Bearer ${memberToken}`;
        return api.request(config);
      }
    }
    
    // Don't retry if already retried or if it's a 4xx error (client error)
    if (config._retry || (error.response && error.response.status >= 400 && error.response.status < 500)) {
      return Promise.reject(error);
    }
    
    // Retry once on network errors or 5xx server errors
    if (!error.response || error.response.status >= 500) {
      config._retry = true;
      // Wait 500ms before retry
      await new Promise(resolve => setTimeout(resolve, 500));
      return api(config);
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const adminLogin = (data) => api.post("/admin/login", data);
export const adminVerify = () => api.get("/admin/verify");

// Dashboard
export const getDashboard = () => api.get("/dashboard");

// Daily Races
export const getDailyRaces = () => api.get("/races");
export const createDailyRace = (data) => api.post("/races", data);
export const updateDailyRace = (id, data) => api.put(`/races/${id}`, data);
export const deleteDailyRace = (id) => api.delete(`/races/${id}`);

// Leaderboard (Current Week Only)
export const getCurrentWeek = () => api.get("/leaderboard/current");
export const getAllRacesLeaderboard = () => api.get("/leaderboard/all-races");
export const getRaceLeaderboard = (raceType) => api.get(`/leaderboard/race/${raceType}`);
// The whole world's board for one daily race. Paged rather than bulk-loaded:
// Race B alone is 48,000 drivers, so this never returns a full field at once.
// `country` and `dr` narrow it, and are sent only when set: an empty string
// would reach the sweeper as a filter on "no country", which is a different
// question from "every country".
export const getGlobalLeaderboard = (raceType, page = 0, limit = 100, filters = {}) =>
  api.get(`/leaderboard/global/${raceType}`, {
    params: {
      page,
      limit,
      ...(filters.country ? { country: filters.country } : {}),
      ...(filters.dr ? { dr: filters.dr } : {}),
    },
  });

// Which countries are actually on this board, most drivers first. Feeds the
// filter, so it can never offer a country that would come back empty.
export const getGlobalCountries = (raceType) =>
  api.get(`/leaderboard/global/${raceType}/countries`);

// One driver's full profile, assembled from Gran Turismo directly. Only
// available on the gt7 source — the scrapers cannot supply a GT7 user id.
// `quick` asks for the half already in our store — this week's races, times
// and positions — and skips the four Gran Turismo requests behind rating,
// career and form. The profile panel asks for both at once and fills itself
// in as each lands, rather than showing nothing until the slowest arrives.
export const getDriverProfile = (userId, { quick = false } = {}) =>
  api.get(`/driver/${userId}${quick ? "?quick=true" : ""}`);
// The logged-in member's own driver profile — the same depth a rival's row
// opens into. Resolved server-side from their PSN id.
export const getMemberGt7Profile = () => api.get("/member/gt7-profile");
export const adminCreateWeek = (data) => api.post("/admin/create-week", data);
export const adminUpdateWeek = (which, data) => api.put(`/admin/update-week/${which}`, data);
export const adminDeactivateWeek = () => api.post("/admin/deactivate-week");
export const adminSetActiveRace = (data) => api.post("/admin/set-active-race", data);
export const adminRefreshLeaderboard = () => api.post("/admin/refresh-leaderboard");
export const adminRefreshLeaderboardSpecific = (raceType) => api.post(`/admin/refresh-leaderboard/${raceType}`);
export const getLeaderboardMaintenance = () => api.get("/leaderboard/maintenance");
export const adminSetLeaderboardMaintenance = (data) => api.post("/admin/leaderboard/maintenance", data);

// Leaderboard Source Toggle (GTSH ↔ DG Edge) + Scraper Health
export const adminGetLeaderboardSource = () => api.get("/admin/leaderboard/source");
export const adminSetLeaderboardSource = (source) => api.post("/admin/leaderboard/source", { source });
export const adminGetScraperHealth = () => api.get("/admin/leaderboard/scraper-health");
// SAGT Slot Machine
export const slotGetAdminConfig = () => api.get("/slot/admin/config");
export const slotSaveDraft = (config) => api.put("/slot/admin/draft", { config });
export const slotPublish = () => api.post("/slot/admin/publish");
export const slotSetWinner = (winner) => api.post("/slot/admin/winner", winner);
export const slotResetStats = (scope = "winner") => api.post("/slot/admin/reset-stats", { scope });
export const slotGetStats = () => api.get("/slot/admin/stats");
export const slotGetPublic = () => api.get("/slot/public");
export const slotGetPublicStats = () => api.get("/slot/public/stats");
export const slotSpin = () => api.post("/slot/spin");
export const slotOverlayGetToken = () => api.get("/slot/overlay/token");
export const slotOverlayPush = (token, results, win) => api.post("/slot/overlay/push", { token, results, win });
export const slotOverlayGetState = (token) => api.get("/slot/overlay/state", { params: { token } });

// ─── SAGT Roulette (SAGT Play, tab #2) ───────────────────
export const rouletteGetAdminConfig = () => api.get("/roulette/admin/config");
export const rouletteSaveDraft = (config) => api.put("/roulette/admin/draft", { config });
export const roulettePublish = () => api.post("/roulette/admin/publish");
export const rouletteResetStats = () => api.post("/roulette/admin/reset-stats");
export const rouletteGetPublic = () => api.get("/roulette/public");
export const rouletteGetPublicStats = () => api.get("/roulette/public/stats");
export const rouletteSpin = (entries) => api.post("/roulette/spin", entries?.length ? { entries } : {});
export const rouletteOverlayGetToken = () => api.get("/roulette/overlay/token");
export const rouletteOverlayPush = (token, items, index, label) =>
  api.post("/roulette/overlay/push", { token, items, index, label });
export const rouletteOverlayGetState = (token) => api.get("/roulette/overlay/state", { params: { token } });
// Scraper Management + Diagnostics (redesigned Leaderboard tab)
export const adminTestScraperSource = (source) => api.post(`/admin/leaderboard/test-source/${source}`);
export const adminGetSourceStatus = () => api.get("/admin/leaderboard/source-status");
export const adminGetScraperDiagnostics = () => api.get("/admin/leaderboard/diagnostics");
export const adminFullRescrape = () => api.post("/admin/leaderboard/full-rescrape");
export const adminGetFullRescrapeStatus = () => api.get("/admin/leaderboard/full-rescrape/status");
export const getGtshDailyRaces = () => api.get("/gtsh/daily-races");
export const getGtshDailyRacesStatus = () => api.get("/gtsh/daily-races/status");
export const adminRefreshDailyRaces = () => api.post("/admin/refresh-daily-races");
export const updateDailyRaceOverrides = (races) => api.put("/admin/daily-races/overrides", { races });
// Next week's actual races, found on Gran Turismo before they open.
export const getNextRound = () => api.get("/daily-races/next");

// One race in depth: medal spread, most-used cars, the time it takes to be
// 100th. Counted by the sweeper across the whole field, not the kept slice.
export const getDailyRaceStats = (raceType) => api.get(`/daily-races/${raceType}/stats`);
export const getDgEdgeLinks = () => api.get("/dg-edge/links");

// OCR Screenshot Upload
export const uploadMemberScreenshot = (formData) =>
  api.post("/admin/upload-member-screenshot", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });

// Members
export const getMembers = () => api.get("/members");
export const addMember = (data) => api.post("/members", data);
export const addMembersBulk = (data) => api.post("/members/bulk", data);
export const deleteMember = (id) => api.delete(`/members/${id}`);
export const syncLeaderboardMembers = () => api.post("/admin/leaderboard/sync-members");
export const updateMember = (id, data) => api.put(`/members/${id}`, data);
export const updateMemberGt7Name = (memberName, gt7Name) => 
  api.put(`/members/by-name/${encodeURIComponent(memberName)}/gt7?gt7_name=${encodeURIComponent(gt7Name)}`);

// Sponsors
// `full` returns the stored base64 logos for admin editing; public pages get cacheable image URLs.
export const getSponsors = (activeOnly = false, full = false) =>
  api.get("/sponsors", { params: { ...(activeOnly && { active_only: true }), ...(full && { full: true }) } });
export const createSponsor = (data) => api.post("/sponsors", data);
export const updateSponsor = (id, data) => api.put(`/sponsors/${id}`, data);
export const deleteSponsor = (id) => api.delete(`/sponsors/${id}`);
export const uploadSponsorLogo = (formData) =>
  api.post("/sponsors/upload-logo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Teams
export const getTeams = (activeOnly = false, full = false) =>
  api.get("/teams", { params: { ...(activeOnly && { active_only: true }), ...(full && { full: true }) } });
export const createTeam = (data) => api.post("/teams", data);
export const uploadTeamBanner = (formData) => api.post("/teams/upload-banner", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const updateTeam = (id, data) => api.put(`/teams/${id}`, data);
export const deleteTeam = (id) => api.delete(`/teams/${id}`);
export const uploadTeamLogo = (formData) =>
  api.post("/teams/upload-logo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Announcements
export const getAnnouncements = () => api.get("/announcements");
export const getPinnedAnnouncements = () => api.get("/announcements/pinned");
export const createAnnouncement = (data) => api.post("/announcements", data);
export const updateAnnouncement = (id, data) => api.put(`/announcements/${id}`, data);
export const deleteAnnouncement = (id) => api.delete(`/announcements/${id}`);

// SAGT Tools
export const getTools = () => api.get("/tools");
export const getAdminTools = () => api.get("/admin/tools");
export const updateTool = (key, data) => api.put(`/tools/${key}`, data);
export const createTool = (data) => api.post("/admin/tools", data);
export const deleteTool = (key) => api.delete(`/admin/tools/${key}`);

// Events
export const getEvents = () => api.get("/events");
export const getPublishedEvents = () => api.get("/events/published");  // Fast, no images
export const getPublishedEventsWithImages = () => api.get("/events/published/with-images");  // Full data with images
export const getEventImage = (id) => api.get(`/events/${id}/image`);
export const createEvent = (data) => api.post("/events", data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/events/${id}`);
export const uploadEventImage = (formData) =>
  api.post("/events/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Championship - SAGT Cup System
export const getChampionshipSeasons = () => api.get("/championship/seasons");
export const getChampionshipSeason = (id) => api.get(`/championship/seasons/${id}`);
export const getActiveChampionship = () => api.get("/championship/seasons/active");
export const createChampionshipSeason = (data) => api.post("/championship/seasons", data);
export const updateChampionshipSeason = (id, data) => api.put(`/championship/seasons/${id}`, data);
export const deleteChampionshipSeason = (id) => api.delete(`/championship/seasons/${id}`);
export const archiveChampionshipSeason = (id) => api.post(`/championship/seasons/${id}/archive`);
export const restoreChampionshipSeason = (id) => api.post(`/championship/seasons/${id}/restore`);
export const duplicateChampionshipSeason = (id) => api.post(`/championship/seasons/${id}/duplicate`);

// Championship Rounds/Calendar
export const getChampionshipRounds = (seasonId) => api.get(`/championship/rounds/${seasonId}`);
export const updateChampionshipRound = (id, data) => api.put(`/championship/rounds/${id}`, data);

// Championship Drivers/Qualification
export const getChampionshipDrivers = (seasonId, division = null) => 
  api.get(`/championship/drivers/${seasonId}${division ? `?division=${division}` : ''}`);
export const createChampionshipDriver = (data) => api.post("/championship/drivers", data);
export const createChampionshipDriversBulk = (data) => api.post("/championship/drivers/bulk", data);
export const updateChampionshipDriver = (id, data) => api.put(`/championship/drivers/${id}`, data);
export const deleteChampionshipDriver = (id) => api.delete(`/championship/drivers/${id}`);
export const lockChampionshipDivisions = (seasonId) => api.post(`/championship/drivers/${seasonId}/lock`);
export const autoAssignDivisions = (seasonId) => api.post(`/championship/drivers/${seasonId}/auto-assign`);
export const importDriversFromLeaderboard = (seasonId, race = "a", topN = 500, assignDivisions = true) =>
  api.post(`/championship/drivers/${seasonId}/import-from-leaderboard?race=${race}&top_n=${topN}&assign_divisions=${assignDivisions}`);
export const syncDivisionsToSeasonStatus = (seasonId) =>
  api.post(`/championship/drivers/${seasonId}/sync-divisions-to-season-status`);
export const reorderDriversByTime = (seasonId) =>
  api.post(`/championship/drivers/${seasonId}/reorder-by-time`);
export const searchMembersForChampionship = (q = "") => api.get(`/championship/search-members?q=${encodeURIComponent(q)}`);
export const createDriverFromMember = (seasonId, memberName, qualiPosition = 0, qualiTime = "", drRating = "", division = "") =>
  api.post(`/championship/drivers/from-member?season_id=${seasonId}&member_name=${encodeURIComponent(memberName)}&quali_position=${qualiPosition}&quali_time=${encodeURIComponent(qualiTime)}&dr_rating=${encodeURIComponent(drRating)}&division=${encodeURIComponent(division)}`);

// Championship Results
export const getChampionshipResults = (seasonId, roundId = null, division = null, publishedOnly = false) => {
  let url = `/championship/results/${seasonId}`;
  const params = [];
  if (roundId) params.push(`round_id=${roundId}`);
  if (division) params.push(`division=${division}`);
  if (publishedOnly) params.push(`published_only=true`);
  params.push(`_t=${Date.now()}`); // cache-bust so the public page always gets fresh published results
  url += `?${params.join('&')}`;
  return api.get(url);
};
export const createChampionshipResult = (data) => api.post("/championship/results", data);
export const createChampionshipResultsBulk = (data) => api.post("/championship/results/bulk", data);
export const updateChampionshipResult = (id, data) => api.put(`/championship/results/${id}`, data);
export const deleteChampionshipResult = (id) => api.delete(`/championship/results/${id}`);
export const publishChampionshipResults = (roundId, division) => api.post(`/championship/results/publish/${roundId}/${division}`);
export const deleteRoundResults = (roundId, division) => api.delete(`/championship/results/round/${roundId}/${division}`);
export const resetSeasonResults = (seasonId) => api.delete(`/championship/results/season/${seasonId}`);

// Championship Standings
export const getChampionshipStandings = (seasonId, division = null, applyDrop = true) => {
  let url = `/championship/standings/${seasonId}`;
  const params = [];
  if (division) params.push(`division=${division}`);
  if (!applyDrop) params.push(`apply_drop=false`);
  params.push(`_t=${Date.now()}`); // cache-bust for fresh standings on the public page
  url += `?${params.join('&')}`;
  return api.get(url);
};
export const getChampionshipStats = (seasonId) => api.get(`/championship/stats/${seasonId}`);

// Championship Sponsors
export const getChampionshipSponsors = (activeOnly = false) => 
  api.get(`/championship/sponsors${activeOnly ? '?active_only=true' : ''}`);
export const createChampionshipSponsor = (data) => api.post("/championship/sponsors", data);
export const updateChampionshipSponsor = (id, data) => api.put(`/championship/sponsors/${id}`, data);
export const deleteChampionshipSponsor = (id) => api.delete(`/championship/sponsors/${id}`);
export const uploadChampionshipSponsorLogo = (formData) =>
  api.post("/championship/sponsors/upload-logo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Championship Liveries
export const getChampionshipLiveries = (seasonId, division = null) =>
  api.get(`/championship/liveries/${seasonId}${division ? `?division=${division}` : ''}`);
export const createChampionshipLivery = (data) => api.post("/championship/liveries", data);
export const updateChampionshipLivery = (id, data) => api.put(`/championship/liveries/${id}`, data);
export const deleteChampionshipLivery = (id) => api.delete(`/championship/liveries/${id}`);
export const uploadChampionshipLivery = (formData) =>
  api.post("/championship/liveries/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// Championship Public Data
export const getChampionshipPublicData = (seasonId) =>
  api.get("/championship/public", { params: { ...(seasonId ? { season_id: seasonId } : {}), _t: Date.now() } });
export const getChampionshipSeasonsList = () => api.get("/championship/seasons");
export const getActiveChampionshipSeason = () => api.get("/championship/seasons/active");

// Registrations
export const getRegistrations = () => api.get("/registrations");
export const createRegistration = (data) => api.post("/registration", data);
export const deleteRegistration = (id) => api.delete(`/registrations/${id}`);
export const exportRegistrations = () => api.get("/registrations/export", { responseType: "blob" });

// CAPTCHA
export const getCaptcha = () => api.get("/captcha/generate");
export const verifyCaptcha = (data) => api.post("/captcha/verify", data);

// Analytics
export const getAnalyticsSummary = () => api.get("/admin/analytics/summary");
export const getAnalyticsDaily = (days = 30) => api.get(`/admin/analytics/daily?days=${days}`);
export const getAnalyticsPages = (days = 30) => api.get(`/admin/analytics/pages?days=${days}`);
export const getAnalyticsCountries = (days = 30) => api.get(`/admin/analytics/countries?days=${days}`);
export const getAnalyticsCountryPages = (days = 30) => api.get(`/admin/analytics/country-pages?days=${days}`);
export const getAnalyticsReferrers = (days = 30) => api.get(`/admin/analytics/referrers?days=${days}`);
export const getAnalyticsRecent = (limit = 50) => api.get(`/admin/analytics/recent?limit=${limit}`);
export const exportAnalytics = (days = 30) => api.get(`/admin/analytics/export?days=${days}`, { responseType: "blob" });

// SAGT Cup Member Authentication
export const memberRegister = (data) => api.post("/member/auth/register", data);
export const memberLogin = (data) => api.post("/member/auth/login", data);
// The demo walkthrough. Only exists where DEMO_MODE is switched on, which is
// why the UI asks first rather than assuming the button belongs on the page.
export const memberDemoAvailable = () => api.get("/member/auth/demo");
export const memberDemoLogin = (psnId) => api.post("/member/auth/demo", { psn_id: psnId });
export const memberVerify = () => api.get("/member/auth/verify");
export const memberDashboard = () => api.get("/member/dashboard");
export const memberSeasonOptIn = () => api.post("/member/season/opt-in");
export const memberSeasonOptOut = () => api.post("/member/season/opt-out");
export const getMemberNotifications = () => api.get("/member/notifications");
export const markMemberNotificationsRead = () => api.post("/member/notifications/mark-read");
export const clearAllMemberNotifications = () => api.delete("/member/notifications/clear-all");
export const memberResetPassword = (data) => api.post("/member/auth/reset-password", data);

// SAGT World (international) dashboard
export const worldDashboard = () => api.get("/world/dashboard");

// Admin - SAGT Cup Members Management
export const getSagtCupMembers = () => api.get("/admin/sagt-cup-members");
export const updateSagtCupMember = (memberId, data) => api.put(`/admin/sagt-cup-members/${memberId}`, data);
export const deleteSagtCupMember = (memberId) => api.delete(`/admin/sagt-cup-members/${memberId}`);

// Admin - SAGT World (Global Accounts)
export const getGlobalAccounts = (params) => api.get("/admin/global-accounts", { params });
export const setMemberAccountType = (memberId, accountType) =>
  api.put(`/admin/member/${memberId}/account-type`, { account_type: accountType });

// Document management
export const listDocuments = () => api.get("/admin/documents");
export const uploadDocument = (docKey, formData) => api.post(`/admin/documents/${docKey}`, formData, {
  headers: { "Content-Type": "multipart/form-data" }
});
export const deleteDocument = (docKey) => api.delete(`/admin/documents/${docKey}`);
export const getDocumentLink = (docKey) => api.get(`/document-links/${docKey}`);
// Document link management (Google Drive URLs)
export const saveDocumentLink = (docKey, url, title, description = "") => api.put(`/admin/document-links/${docKey}`, { url, title, description });
export const deleteDocumentLink = (docKey) => api.delete(`/admin/document-links/${docKey}`);
export const getSagtCupMemberSeasons = (memberId) => api.get(`/admin/sagt-cup-members/${memberId}/seasons`);
export const getSagtCupSeasonEntries = (seasonId) => api.get(`/admin/sagt-cup-season-entries/${seasonId}`);
export const updateSagtCupSeasonEntry = (entryId, data) => api.put(`/admin/sagt-cup-season-entry/${entryId}`, data);
export const bulkUpdateSagtCupSeasonEntries = (updates) => api.put("/admin/sagt-cup-season-entries/bulk", updates);
export const generateMemberResetToken = (memberId) => api.post(`/admin/member/reset-token/${memberId}`);
export const createSagtCupSeasonEntry = (memberId, seasonId) => api.post(`/admin/sagt-cup-season-entry?member_id=${memberId}&season_id=${seasonId}`);

// Badge System
export const getAllBadges = () => api.get("/badges");
export const getSelectableBadges = () => api.get("/badges/selectable");
export const adminSetBadgeAdminOnly = (badgeId, adminOnly) =>
  api.put(`/admin/badges/${badgeId}/admin-only`, { admin_only: adminOnly });
export const getMemberShortlistedBadges = () => api.get("/member/shortlisted-badges");
export const updateMemberShortlistedBadges = (badgeIds) =>
  api.put("/member/shortlisted-badges", { badge_ids: badgeIds });
export const getRecentBadges = (limit = 10) => api.get(`/badges/recent?limit=${limit}`);
export const getTrendingShortlistedBadges = (limit = 5) =>
  api.get(`/badges/trending-shortlisted?limit=${limit}`);
export const getMemberBadges = () => api.get("/member/badges");
export const getMemberDisplayBadges = () => api.get("/member/display-badges");
export const updateMemberDisplayBadges = (badgeIds) => api.put("/member/display-badges", { badge_ids: badgeIds });
export const updateMemberRacingName = (racingName) => api.put("/member/racing-name", { racing_name: racingName });
export const updateMemberProfile = (data) => api.put("/member/profile", data);
export const getMemberLeaderboardStatus = () => api.get("/member/leaderboard-status");

// Badge Icons Management (Admin)
export const adminGetBadgeIconsStatus = () => api.get("/admin/badges/icons");
export const adminUploadBadgeIcon = (badgeId, formData) => 
  api.post(`/admin/badges/${badgeId}/icon`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const adminDeleteBadgeIcon = (badgeId) => api.delete(`/admin/badges/${badgeId}/icon`);
export const adminClearAllBadgeIcons = () => api.delete("/admin/badges/icons/all");

// Public Members List & Profile
export const getPublicMembersList = () => api.get("/public/members");
export const getPublicMemberProfile = (memberId) => api.get(`/public/members/${memberId}`);

// Esports Team (public)
export const getEsportsTeam = () => api.get("/esports-team");
export const getEsportsSponsors = () => api.get("/esports-team/sponsors");

// Esports Team (admin)
export const adminGetEsportsTeam = () => api.get("/admin/esports-team");
export const adminAddEsportsTeamMember = (data) => api.post("/admin/esports-team", data);
export const adminRemoveEsportsTeamMember = (memberId) => api.delete(`/admin/esports-team/${memberId}`);
export const adminUpdateEsportsDriverSocials = (memberId, data) => api.put(`/admin/esports-team/${memberId}/socials`, data);
export const adminSetEsportsDriverRole = (memberId, role) => api.put(`/admin/esports-team/${memberId}/role`, { role });
export const adminUploadEsportsDriverHeadshot = (memberId, formData) =>
  api.post(`/admin/esports-team/${memberId}/headshot`, formData, { headers: { "Content-Type": "multipart/form-data" } });
export const adminRemoveEsportsDriverHeadshot = (memberId) => api.delete(`/admin/esports-team/${memberId}/headshot`);
export const adminCreateEsportsSponsor = (data) => api.post("/esports-team/sponsors", data);
export const adminUpdateEsportsSponsor = (sponsorId, data) => api.put(`/esports-team/sponsors/${sponsorId}`, data);
export const adminDeleteEsportsSponsor = (sponsorId) => api.delete(`/esports-team/sponsors/${sponsorId}`);
export const adminUploadEsportsSponsorLogo = (formData) =>
  api.post("/esports-team/sponsors/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } });

// Esports community channels
export const getEsportsTeamChannels = () => api.get("/esports-team/channels");
export const adminUpdateEsportsTeamChannels = (data) => api.put("/admin/esports-team/channels", data);

// Esports events
export const getEsportsEvents = () => api.get("/esports-team/events");
export const adminGetEsportsEvents = () => api.get("/admin/esports-team/events");
export const adminCreateEsportsEvent = (data) => api.post("/admin/esports-team/events", data);
export const adminUpdateEsportsEvent = (id, data) => api.put(`/admin/esports-team/events/${id}`, data);
export const adminDeleteEsportsEvent = (id) => api.delete(`/admin/esports-team/events/${id}`);

export const adminGetMemberBadges = (memberId) => api.get(`/admin/members/${memberId}/badges`);
export const adminAwardBadge = (memberId, badgeId, metadata = null) => api.post(`/admin/members/${memberId}/badges`, { badge_id: badgeId, metadata });
export const adminRemoveBadge = (memberId, awardId) => api.delete(`/admin/members/${memberId}/badges/${awardId}`);
export const adminGetMembersBadgeSummary = () => api.get("/admin/members/badges/summary");

// Member-Leaderboard Linking System
export const getMemberLinks = () => api.get("/admin/member-links");
export const getLeaderboardDrivers = () => api.get("/admin/leaderboard-drivers");
export const createMemberLink = (sagtMemberId, leaderboardDriverId, leaderboardPsnId = null) => 
  api.post("/admin/member-links", { 
    sagt_member_id: sagtMemberId, 
    leaderboard_driver_id: leaderboardDriverId,
    leaderboard_psn_id: leaderboardPsnId 
  });
export const updateMemberLink = (linkId, data) => api.put(`/admin/member-links/${linkId}`, null, { params: data });
export const deleteMemberLink = (linkId) => api.delete(`/admin/member-links/${linkId}`);
export const getTopRegisteredDrivers = (seasonId, topN = 62, race = "a") => 
  api.get(`/admin/top-registered-drivers?season_id=${seasonId}&top_n=${topN}&race=${race}`);

// Manual Driver Management
export const addManualDriver = (data) => api.post("/admin/leaderboard-drivers/manual", data);
export const deleteManualDriver = (driverId) => api.delete(`/admin/leaderboard-drivers/manual/${encodeURIComponent(driverId)}`);
export const updateManualDriver = (driverId, data) => api.put(`/admin/leaderboard-drivers/manual/${encodeURIComponent(driverId)}`, data);

// Abroad PSN scraper exceptions (SA drivers living abroad)
export const getAbroadPsnExceptions = () => api.get("/admin/abroad-psn-exceptions");
export const addAbroadPsnException = (psnId) => api.post("/admin/abroad-psn-exceptions", { psn_id: psnId });
export const deleteAbroadPsnException = (psnId) => api.delete(`/admin/abroad-psn-exceptions/${encodeURIComponent(psnId)}`);

// Rival System
export const getMemberRival = () => api.get("/member/rival");
export const setMemberRivals = (rivalDriverIds) => api.post("/member/rival", { rival_driver_ids: rivalDriverIds });
export const setSelfDriver = (selfDriverId) => api.post("/member/rival/self", { self_driver_id: selfDriverId });
export const searchRivalDrivers = (q = "") => api.get(`/member/rival/search?q=${encodeURIComponent(q)}`);
export const setMemberRivalPin = (race) => api.post("/member/rival/pin", { race: race || "" });

// ─── Private League API ───────────────────────────────────────────────
const plApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 30000 });

// Cheap client-side JWT expiry check. Returns true when the token is
// unmistakably expired so we can prune it BEFORE the backend rejects the
// request. This is what prevents the "logged in, kicked out after a few
// seconds" bug: a leftover `pl_ceo_token` from a previous god-mode session
// (or a previous account) whose 12-hour lifetime had already lapsed used to
// get attached to every request. When the backend returned 401, the response
// interceptor wiped BOTH tokens and bounced the driver back to the login
// page — even though their fresh `pl_token` was still perfectly valid.
const _isJwtExpired = (token) => {
  if (!token || typeof token !== "string") return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false; // no exp claim → treat as long-lived
    // 5s of clock-skew slack.
    return payload.exp * 1000 < Date.now() - 5000;
  } catch {
    return true; // malformed → treat as expired
  }
};

const _readPlTokens = () => {
  let token = localStorage.getItem("pl_token");
  let ceoToken = localStorage.getItem("pl_ceo_token");
  if (token && _isJwtExpired(token)) {
    localStorage.removeItem("pl_token");
    token = null;
  }
  if (ceoToken && _isJwtExpired(ceoToken)) {
    // A stale CEO token from an old god-mode session is the exact thing
    // that used to kick drivers back to the login page. Drop it silently.
    localStorage.removeItem("pl_ceo_token");
    ceoToken = null;
  }
  return { token, ceoToken };
};

plApi.interceptors.request.use((config) => {
  const { token, ceoToken } = _readPlTokens();
  // GET reads work with either a driver OR CEO token (backend `verify_pl_user`
  // accepts both). PREFER the driver token so a stale/soon-expired CEO token
  // never sabotages a normal browsing session — this is the core of the
  // "kicked out after a few seconds" fix. Mutations (POST/PUT/DELETE) and the
  // CEO-verify endpoint still prefer the CEO token because those routes
  // explicitly require the `ceo` role.
  const method = (config.method || "get").toLowerCase();
  const isMutation = method !== "get" && method !== "head";
  const isCeoEndpoint = !!(config.url && (config.url.includes("/pl/ceo") || config.url.includes("/pl/registrations") || config.url.includes("/pl/alltime")));
  const preferCeo = isMutation || isCeoEndpoint;
  const chosen = preferCeo ? (ceoToken || token) : (token || ceoToken);
  if (chosen) {
    config.headers.Authorization = `Bearer ${chosen}`;
  }
  return config;
});

// Response interceptor — when the backend reports a stale/expired/invalid token
// (typically because admin rotated the league credentials), wipe stored tokens
// and bounce back to the login page so the driver/CEO is forced to re-authenticate
// with the new credentials instead of seeing a half-broken UI.
plApi.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Never bounce the login/verify endpoints themselves — those legitimately
    // return 401 for "wrong password" and should be handled by the form UI.
    const isAuthCall = url.includes("/pl/login") || url.includes("/pl/ceo-verify");
    if (status === 401 && !isAuthCall) {
      // If the driver's own `pl_token` is still valid, only the CEO token was
      // the problem — drop just that one so the driver stays signed in and
      // simply loses god-mode. This prevents the "kicked back to login after
      // trying a CEO-only action" whiplash that used to happen when a CEO
      // token expired 12h into a 24h driver session.
      const driverToken = localStorage.getItem("pl_token");
      if (driverToken && !_isJwtExpired(driverToken)) {
        localStorage.removeItem("pl_ceo_token");
      } else {
        localStorage.removeItem("pl_token");
        localStorage.removeItem("pl_ceo_token");
        if (typeof window !== "undefined" && !window.location.pathname.includes("/leagues/login") && !window.location.pathname.includes("/private-league/login")) {
          window.location.replace("/leagues/login");
        }
      }
    }
    return Promise.reject(err);
  }
);

export const plLogin = (username, password) => plApi.post("/pl/login", { username, password });
export const plCeoVerify = (password) => plApi.post("/pl/ceo-verify", { password });
export const plGetPublic = () => plApi.get("/pl/public");
export const plGetSeasons = () => plApi.get("/pl/seasons");
export const plGetActiveSeason = () => plApi.get("/pl/seasons/active");
// All-Time Stats
export const plGetAlltimeConfig = () => plApi.get("/pl/alltime/config");
export const plUpdateAlltimeConfig = (data) => plApi.put("/pl/alltime/config", data);
export const plGetAlltimeStats = () => plApi.get("/pl/alltime/stats");
export const plCreateSeason = (data) => plApi.post("/pl/seasons", data);
export const plUpdateSeason = (id, data) => plApi.put(`/pl/seasons/${id}`, data);
export const plDeleteSeason = (id) => plApi.delete(`/pl/seasons/${id}`);
export const plArchiveSeason = (id) => plApi.put(`/pl/seasons/${id}/archive`);
export const plGetRounds = (seasonId) => plApi.get(`/pl/rounds/${seasonId}`);
export const plUpdateRound = (roundId, data) => plApi.put(`/pl/rounds/${roundId}`, data);
export const plGetDrivers = (seasonId) => plApi.get(`/pl/drivers/${seasonId}`);
export const plCreateDriver = (data) => plApi.post("/pl/drivers", data);
export const plUpdateDriver = (id, data) => plApi.put(`/pl/drivers/${id}`, data);
export const plGetSlot = () => plApi.get("/pl/slot");
export const plSetSlot = (reels) => plApi.put("/pl/slot", { reels });
export const plAddSlotHistory = (result) => plApi.post("/pl/slot/history", { result });
export const plEditSlotHistory = (id, label) => plApi.put(`/pl/slot/history/${id}`, { label });
export const plDeleteSlotHistory = (id) => plApi.delete(`/pl/slot/history/${id}`);
export const plGetTeamRandomiser = () => plApi.get("/pl/team-randomiser");
export const plSetTeamRandomiser = (cfg) => plApi.put("/pl/team-randomiser", cfg);
export const plAddRandomiserHistory = (payload) => plApi.post("/pl/team-randomiser/history", payload);
export const plEditRandomiserHistory = (id, label) => plApi.put(`/pl/team-randomiser/history/${id}`, { label });
export const plDeleteRandomiserHistory = (id) => plApi.delete(`/pl/team-randomiser/history/${id}`);
export const plDeleteDriver = (id) => plApi.delete(`/pl/drivers/${id}`);
export const plGetResults = (seasonId, roundId, lobby) => plApi.get(`/pl/results/${seasonId}${roundId ? `?round_id=${roundId}${lobby ? `&lobby=${lobby}` : ''}` : ''}`);
export const plCreateResultsBulk = (results) => plApi.post("/pl/results/bulk", results);
export const plDeleteRoundResults = (roundId) => plApi.delete(`/pl/results/round/${roundId}`);
export const plGetStandings = (seasonId, drop = true) => plApi.get(`/pl/standings/${seasonId}?drop=${drop}`);
export const plGetFines = (seasonId) => plApi.get(`/pl/fines/${seasonId}`);
export const plSetFines = (seasonId, fines) => plApi.put(`/pl/fines/${seasonId}`, fines);
export const plDuplicateSeason = (seasonId, name) => plApi.post(`/pl/seasons/${seasonId}/duplicate`, { name });
export const plUploadLogo = (formData) => plApi.post("/pl/branding/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const plSetSeasonLogo = (seasonId, logoUrl) => plApi.put(`/pl/seasons/${seasonId}/logo`, { logo_url: logoUrl });
export const plGetCarChoices = (seasonId) => plApi.get(`/pl/seasons/${seasonId}/car-choices`);
export const plSetCarChoice = (seasonId, driverId, carBrand) => plApi.put(`/pl/seasons/${seasonId}/car-choices/${driverId}`, { car_brand: carBrand });
export const plUpdateBranding = (data) => plApi.put("/pl/branding", data);
export const plGetBranding = () => plApi.get("/pl/branding");
export const plGetTeams = (seasonId) => plApi.get(`/pl/teams/${seasonId}`);
export const plCreateTeam = (seasonId, name) => plApi.post("/pl/teams", { season_id: seasonId, name });
export const plRenameTeam = (id, name) => plApi.put(`/pl/teams/${id}`, { name });
export const plDeleteTeam = (id) => plApi.delete(`/pl/teams/${id}`);
export const plSetDriverTeam = (driverId, teamId) => plApi.put(`/pl/drivers/${driverId}/team`, { team_id: teamId || null });
export const plSetTeamsEnabled = (seasonId, enabled) => plApi.put(`/pl/seasons/${seasonId}/teams-enabled`, { enabled });
export const plSetTeamsScope = (seasonId, scope, roundIds) => plApi.put(`/pl/seasons/${seasonId}/teams-scope`, { scope, round_ids: roundIds });
export const plGetTeamStandings = (seasonId, drop = true) => plApi.get(`/pl/team-standings/${seasonId}?drop=${drop}`);
export const plUpdatePointsSystem = (seasonId, points, bonusPoints, perLobby) => {
  const body = { points_system: points };
  if (bonusPoints) body.bonus_points_system = bonusPoints;
  if (perLobby) {
    body.per_lobby_points = !!perLobby.per_lobby_points;
    body.lobby_points_systems = perLobby.lobby_points_systems || {};
    body.lobby_bonus_points_systems = perLobby.lobby_bonus_points_systems || {};
  }
  return plApi.put(`/pl/seasons/${seasonId}/points`, body);
};
export const adminGetPLConfig = () => api.get("/admin/pl/config");
export const adminUpdatePLConfig = (data) => api.put("/admin/pl/config", data);
export const adminGetPLAccounts = () => api.get("/admin/pl/accounts");
export const adminCreatePLAccount = (data) => api.post("/admin/pl/accounts", data);
export const adminUpdatePLAccount = (id, data) => api.put(`/admin/pl/accounts/${id}`, data);
export const adminDeletePLAccount = (id) => api.delete(`/admin/pl/accounts/${id}`);
export const adminPLForceSignoutPrimary = () => api.post("/admin/pl/config/force-signout");
export const adminPLForceSignoutAccount = (id) => api.post(`/admin/pl/accounts/${id}/force-signout`);

// ─── Private League: Public Registration & CEO Approval ──────
// Public (no auth) — used by /private-league/login
const publicPlApi = axios.create({ baseURL: `${BACKEND_URL}/api`, timeout: 20000 });
export const plPublicListLeagues = () => publicPlApi.get("/pl/public/leagues");
export const plPublicSubmitRegistration = (accountId, data, signal) =>
  publicPlApi.post(`/pl/public/leagues/${accountId}/register`, data, { signal });
// CEO — used inside CEO Mode → Drivers tab
export const plCeoListRegistrations = (status) =>
  plApi.get(`/pl/registrations${status ? `?status=${status}` : ""}`);
export const plCeoAcceptRegistration = (regId, seasonId, lobby) =>
  plApi.post(`/pl/registrations/${regId}/accept`, { season_id: seasonId || "", lobby: lobby ?? 0 });
export const plCeoRejectRegistration = (regId) => plApi.post(`/pl/registrations/${regId}/reject`);
export const plCeoWaitlistRegistration = (regId) => plApi.post(`/pl/registrations/${regId}/waitlist`);
export const plCeoPendingRegistration = (regId) => plApi.post(`/pl/registrations/${regId}/pending`);
export const plCeoDeleteRegistration = (regId) => plApi.delete(`/pl/registrations/${regId}`);
export const plCeoGetRegistrationsOpen = () => plApi.get("/pl/registrations-open");
export const plCeoSetRegistrationStatus = (status) => plApi.put("/pl/registrations-open", { status });
export const plCeoReorderRegistrations = (ids) => plApi.post("/pl/registrations/reorder", { order: ids });

// SAGT member — their own PL registrations
export const memberListPLRegistrations = () => api.get("/member/pl-registrations");
export const memberDeletePLRegistration = (regId) => api.delete(`/member/pl-registrations/${regId}`);

// Admin — reorder public leagues + primary GTWC card config
export const adminReorderPLAccounts = (idsInOrder) =>
  api.post("/admin/pl/accounts/reorder", { order: idsInOrder });
export const adminGetPLPrimaryPublic = () => api.get("/admin/pl/primary-public");
export const adminUpdatePLPrimaryPublic = (data) => api.put("/admin/pl/primary-public", data);

// ─── Orphan Cleanup (admin) ────────────────────────────────────
export const adminOrphanCleanupPreview = () => api.get("/admin/orphan-cleanup/preview");
export const adminOrphanCleanupRun = () => api.post("/admin/orphan-cleanup/run");

// ─── Track-name overrides (admin) ─────────────────────────────
export const adminGetTrackNameOverrides = () => api.get("/admin/track-name-overrides");
export const adminSetTrackNameOverrides = (overrides) => api.post("/admin/track-name-overrides", overrides);

// ─── Member Linking (admin flags) ──────────────────────────────
export const adminGetLinkingFlags = () => api.get("/admin/linking/flags");

// ─── SAGT Rating Engine ────────────────────────────────────────
export const adminGetSagtRatingStatus = () => api.get("/admin/sagt-rating/status");
export const adminRecalculateSagtHistory = () => api.post("/admin/sagt-rating/recalculate-history", {}, { timeout: 120000 });
export const getSagtRatingRules = () => api.get("/rankings/sagt-rating/rules");
export const getSagtPositionPoints = (position) => api.get("/rankings/sagt-rating/position-points", { params: { position } });
export const adminGetPositionTransition = () => api.get("/admin/sagt-rating/position-transition");
export const adminRunPositionTransition = (commit = false) => api.post("/admin/sagt-rating/position-transition", { commit }, { timeout: 180000 });
export const adminTrimScoringHistory = (keepLatestWeeks = 2) => api.post("/admin/sagt-rating/trim-history", { keep_latest_weeks: keepLatestWeeks }, { timeout: 180000 });
export const adminGetPenaltySettings = () => api.get("/admin/sagt-rating/penalty-settings");
export const adminUpdatePenaltySettings = (penaltyPerMissedWeek) => api.put("/admin/sagt-rating/penalty-settings", { penalty_per_missed_week: penaltyPerMissedWeek });
export const adminRecaptureWeek = (weekKey, republish = true) => api.post("/admin/sagt-rating/recapture-week", { week_key: weekKey, republish }, { timeout: 180000 });
export const adminDriverBreakdown = (query) => api.get("/admin/sagt-rating/driver-breakdown", { params: { q: query } });
export const adminRaceFieldAudit = (weekKey, race) => api.get("/admin/sagt-rating/race-field", { params: { week_key: weekKey, race } });
export const getSagtRatingMaintenance = () => api.get("/rankings/sagt-rating/maintenance");
export const adminGetSagtRatingMaintenance = () => api.get("/admin/sagt-rating/maintenance");
export const adminSetSagtRatingMaintenance = (enabled, message) => api.put("/admin/sagt-rating/maintenance", { enabled, message });
export const adminGetSagtScores = () => api.get("/admin/sagt-rating/scores");
export const adminGetSagtRatingFailures = () => api.get("/admin/sagt-rating/failures");
export const adminRunSagtRatingUpdate = (payload = {}) => api.post("/admin/sagt-rating/run", payload);
export const adminGetSagtHealthWebhook = () => api.get("/admin/sagt-rating/health-webhook");
export const adminSetSagtHealthWebhook = (url) => api.put("/admin/sagt-rating/health-webhook", { url });
export const adminRunSagtHealthCheck = () => api.post("/admin/sagt-rating/health-check");
export const adminPreviewSagtRating = (payload = {}) => api.post("/admin/sagt-rating/preview", payload);
export const adminGetSagt2Weeks = () => api.get("/admin/sagt-rating/weeks");
export const adminGetSagt2WeekDetail = (weekKey) => api.get(`/admin/sagt-rating/weeks/${weekKey}`);
export const adminToggleForceImmortal = (memberId, enabled) => api.post(`/admin/sagt-rating/force-immortal/${memberId}`, { enabled });
export const adminDeleteSagtScore = (memberId) => api.delete(`/admin/sagt-rating/scores/${memberId}`);
export const adminDiagnoseDriverRating = (q) => api.get(`/admin/sagt-rating/diagnose`, { params: { q } });

// ── SAGT Rating v3 ────────────────────────────────────────────────────────
export const v3GetSagtStatus = () => api.get("/admin/sagt-rating/v3/status");
export const v3CaptureSnapshot = (weekKey, force = false) => api.post("/admin/sagt-rating/v3/capture-snapshot", { week_key: weekKey, force }, { timeout: 60000 });
export const v3PublishRatings = (scoringWindow = 8) => api.post("/admin/sagt-rating/v3/publish", { scoring_window: scoringWindow }, { timeout: 60000 });
export const v3ListSnapshots = () => api.get("/admin/sagt-rating/v3/snapshots");
export const v3DeleteSnapshot = (weekKey) => api.delete(`/admin/sagt-rating/v3/snapshot/${weekKey}`);
export const v3DriverBreakdown = (query) => api.get("/admin/sagt-rating/v3/driver-breakdown", { params: { q: query } });
export const v3RaceFieldAudit = (weekKey, race) => api.get("/admin/sagt-rating/v3/race-field", { params: { week_key: weekKey, race } });
export const v3GetDivisionConfig = () => api.get("/admin/sagt-rating/v3/division-config");
export const v3SetDivisionConfig = (config) => api.put("/admin/sagt-rating/v3/division-config", { config });
export const v3GetScoringConfig = () => api.get("/admin/sagt-rating/v3/scoring-config");
export const v3SetScoringConfig = (window) => api.put("/admin/sagt-rating/v3/scoring-config", { window });
export const v3BackfillFromTenths = (weekKeys) => api.post("/admin/sagt-rating/v3/backfill-from-tenths", { week_keys: weekKeys }, { timeout: 120000 });

// Public Rankings (SAGT Rating leaderboard)
export const getPublicSagtRanking = () => api.get("/rankings/sagt-rating"); // Public leaderboard
export const getPublicDivisionThresholds = () => api.get("/rankings/division-thresholds");
export const getDriverOfTheWeek = () => api.get("/rankings/driver-of-the-week");
export const markMilestoneCelebrated = (notificationId) =>
  api.post("/member/notifications/milestone-celebrated", { notification_id: notificationId });

// Admin: SAGT Rankings page explainer visibility toggle
export const adminGetExplainerVisibility = () => api.get("/admin/sagt-rating/explainer-visibility");
export const adminSetExplainerVisibility = (show) => api.put("/admin/sagt-rating/explainer-visibility", { show });
export const adminGetDivisionConfig = () => api.get("/admin/sagt-rating/division-config");
export const adminSetDivisionConfig = (payload) => api.put("/admin/sagt-rating/division-config", payload);

// Logged-in member's SAGT stats (for My Stats tab)
export const getMemberSagtStats = () => api.get("/member/sagt-stats");

// ─── SAGT Academy ─────────────────────────────────────────────────
export const submitAcademyApplication = (data) => api.post("/academy/applications", data);
// Admin — Academy
export const adminListAcademyApplications = () => api.get("/admin/academy/applications");
export const adminGetAcademyApplication = (id) => api.get(`/admin/academy/applications/${id}`);
export const adminUpdateAcademyApplication = (id, data) => api.post(`/admin/academy/applications/${id}/update`, data);
export const adminDeleteAcademyApplication = (id) => api.delete(`/admin/academy/applications/${id}`);
export const adminListCoaches = () => api.get("/admin/academy/coaches");
export const adminCreateCoach = (data) => api.post("/admin/academy/coaches", data);
export const adminUpdateCoach = (id, data) => api.put(`/admin/academy/coaches/${id}`, data);
export const adminDeleteCoach = (id) => api.delete(`/admin/academy/coaches/${id}`);

// Discord invite
export const getAcademyDiscordInvite = () => api.get("/academy/discord-invite");
export const adminSetAcademyDiscordInvite = (invite_url) => api.put("/admin/academy/discord-invite", { invite_url });

// Coaching contact email
export const getAcademyCoachingEmail = () => api.get("/academy/coaching-email");
export const adminSetAcademyCoachingEmail = (email) => api.put("/admin/academy/coaching-email", { email });

// Hot Race (auto + override)
export const adminGetHotRace = () => api.get("/admin/leaderboard/hot-race");
export const adminSetHotRaceOverride = (race) => api.put("/admin/leaderboard/hot-race-override", { race });

// Coach portal
export const coachLogin = (data) => api.post("/coach/login", data);
export const coachMe = () => api.get("/coach/me");
export const coachListApplications = () => api.get("/coach/applications");

// Coach Sessions
export const coachCreateSessions = (appId, payload) =>
  api.post(`/coach/applications/${appId}/sessions`, payload);
export const coachUpdateSession = (appId, sessionId, payload) =>
  api.post(`/coach/applications/${appId}/sessions/${sessionId}`, payload);
export const coachDeleteSession = (appId, sessionId) =>
  api.delete(`/coach/applications/${appId}/sessions/${sessionId}`);
export const coachBulkUpdateSessions = (appId, payload) =>
  api.post(`/coach/applications/${appId}/sessions/bulk-update`, payload);
export const coachBulkDeleteSessions = (appId, payload) =>
  api.post(`/coach/applications/${appId}/sessions/bulk-delete`, payload);

// Coach driver notes
export const coachUpdateNotes = (appId, notes) =>
  api.post(`/coach/applications/${appId}/notes`, { notes });

// Coach portal — dashboard, sessions & hours, applications visibility, transfers
export const coachDashboard = () => api.get("/coach/dashboard");
export const coachAllSessions = () => api.get("/coach/sessions");
export const coachAllApplications = () => api.get("/coach/academy/applications");
export const coachActiveCoaches = () => api.get("/coach/coaches");
export const coachTransferDriver = (appId, newCoachId) =>
  api.post(`/coach/applications/${appId}/transfer`, { new_coach_id: newCoachId });
export const coachSetDriverStatus = (appId, driverStatus) =>
  api.patch(`/coach/applications/${appId}/driver-status`, { driver_status: driverStatus });

// Admin — academy oversight
export const adminAcademyOverview = () => api.get("/admin/academy/overview");
export const adminAcademyTransfers = () => api.get("/admin/academy/transfers");

export default api;
